import { AnimatePresence, motion } from "framer-motion";
import { type DebouncedFunc } from "lodash";
import type {
  FC,
  RefObject,
  Dispatch,
  SetStateAction,
  MutableRefObject,
} from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import IconSearch from "../../../assets/icons/IconSearch";
import IconPin from "../../../assets/icons/IconPin";
import IconStructure from "../../../assets/icons/IconStructure";
import IconCrosshair from "../../../assets/icons/IconCrosshair";
import IconSearchError from "../../../assets/icons/IconSearchError";
import IconNew from "../../../assets/TakyonDesignSystem/components/Icon/Icon";

import { type AutocompleteLocation } from "../../../assets/Utils/search";
import { useSearch, DEFAULT_PARAMS } from "../../hooks/useSearch";
import imageSeaside from "../../../assets/img/directsearch/seaside.avif";
import imageMountains from "../../../assets/img/directsearch/mountains.avif";
import imageCity from "../../../assets/img/directsearch/city.avif";
import imageLake from "../../../assets/img/directsearch/lake.avif";
import imageHinterland from "../../../assets/img/directsearch/hinterland.avif";
import imageWellness from "../../../assets/img/directsearch/wellness.avif";
import { ScrollLock } from "./Search";

const directSearchCategories = [
  {
    key: "sea",
    image: imageSeaside,
    label: "destination_category_sea",
  },
  {
    key: "mountain",
    image: imageMountains,
    label: "destination_category_mountain",
  },
  {
    key: "city",
    image: imageCity,
    label: "destination_category_city",
  },
  {
    key: "lake",
    image: imageLake,
    label: "destination_category_lake",
  },
  {
    key: "countryside",
    image: imageHinterland,
    label: "destination_category_countryside",
  },
  {
    key: "wellness",
    image: imageWellness,
    label: "destination_category_wellness",
  },
] as const;

type DestinationModalProps = {
  isLocationOpen: boolean;
  locationRef?: RefObject<HTMLDivElement>;
  shouldOpenOver?: boolean;
  mode: "mobile" | "desktop";
  parent: "home" | "nav" | "detail";
  inputText: string;
  setInputText: Dispatch<SetStateAction<string>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  searchCallbackDebounced: MutableRefObject<
    DebouncedFunc<(query: string) => Promise<void>>
  >;
  setIsLocationOpen: Dispatch<SetStateAction<boolean>>;
  locations: AutocompleteLocation[];
  locationsPerCategory: {
    places: AutocompleteLocation[];
    structures: AutocompleteLocation[];
    extended: AutocompleteLocation[];
  };
  setIsCalendarOpen: Dispatch<SetStateAction<boolean>>;
  setIsGuestsOpen: Dispatch<SetStateAction<boolean>>;
};

const DestinationModal: FC<DestinationModalProps> = ({
  isLocationOpen,
  locationRef,
  shouldOpenOver,
  mode,
  parent,
  inputText,
  setInputText,
  isLoading,
  setIsLoading,
  searchCallbackDebounced,
  setIsLocationOpen,
  locations,
  locationsPerCategory,
  setIsCalendarOpen,
  setIsGuestsOpen,
}) => {
  const { t } = useTranslation();

  const { searchParameters, setParameters, isFirstSearch } = useSearch();

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleOutsideInteraction = (e: TouchEvent | MouseEvent) => {
      const input = inputRef.current;
      if (!input) return;

      if (
        document.activeElement === input &&
        !input.contains(e.target as Node)
      ) {
        input.blur();
      }
    };

    if (isLocationOpen) {
      document.addEventListener("touchstart", handleOutsideInteraction, {
        passive: true,
      });
      document.addEventListener("mousedown", handleOutsideInteraction);
    }

    return () => {
      document.removeEventListener("touchstart", handleOutsideInteraction);
      document.removeEventListener("mousedown", handleOutsideInteraction);
    };
  }, [isLocationOpen]);

  return (
    <AnimatePresence>
      {isLocationOpen && (
        <>
          <ScrollLock />
          <motion.div
            ref={locationRef} // Add ref here
            className={`flex flex-column ${
              mode === "desktop" ? " rounded-3xl" : ""
            }`}
            style={
              mode === "desktop"
                ? {
                    position: "absolute",
                    ...(shouldOpenOver
                      ? { bottom: parent !== "nav" ? "115%" : "140%" }
                      : { top: parent !== "nav" ? "115%" : "140%" }),
                    left: "-0.5rem",
                    zIndex: 100,
                    overflow: "hidden",
                    border: "1px solid lightgray",
                    boxShadow: "4px 4px 10px 0px rgba(0, 0, 0, 0.05)",
                    background: "white",
                    minWidth: inputText === "" ? "400px" : "550px",
                  }
                : {
                    position: "fixed",
                    top: "0px",
                    left: 0,
                    right: 0,
                    zIndex: 9999,
                    overflow: "hidden",
                    border: "1px solid lightgray",
                    boxShadow: "4px 4px 10px 0px rgba(0, 0, 0, 0.05)",
                    background: "white",
                    height: "100dvh",
                  }
            }
            initial={
              mode === "desktop" ? { opacity: 0 } : { y: 100, opacity: 0 }
            }
            animate={mode === "desktop" ? { opacity: 1 } : { y: 0, opacity: 1 }}
            exit={
              mode === "desktop"
                ? { y: -20, opacity: 0 }
                : { y: 100, opacity: 0 }
            }
            transition={{ duration: 0.2 }}
          >
            {mode === "mobile" && (
              <div className="relative flex justify-end px-3 items-center box-border border-bottom h-16 gap-8 shrink-0">
                <div className="relative flex-1">
                  <IconSearch className="size-3.5 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    autoComplete="off"
                    id="search-location-input-mobile"
                    placeholder={t("ds.destination_ph")}
                    value={inputText}
                    type="custom"
                    autoFocus
                    onChange={(e) => {
                      setInputText(e.target.value);

                      if (e.target.value !== "") {
                        setIsLoading(true);
                        searchCallbackDebounced.current(e.target.value);
                      }
                    }}
                    className="bg-gray-100 rounded-lg pl-9 py-2 outline-none border-0 text-sm w-full"
                    ref={inputRef}
                  />
                  {inputText !== "" && (
                    <button
                      type="button"
                      onClick={() => setInputText("")}
                      className="bg-[#747474] border-none rounded-full size-4 flex items-center justify-center absolute right-3 top-1/2 -translate-y-1/2 touch-hitbox"
                    >
                      <IconNew icon="close" className="size-3 fill-white" />
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setIsLocationOpen(false)}
                  className="bg-transparent border-none touch-hitbox relative"
                >
                  <IconNew icon="close" className="size-5" />
                </button>
              </div>
            )}
            {inputText === "" && (
              <div className="flex flex-col px-3 gap-2 mt-4 lg:mt-0 lg:p-4 h-full overflow-y-auto">
                <h3 className="text-[#747474] text-sm font-medium ">
                  {t("ds.destination_categories_title")}
                </h3>
                <ul className="grid gap-1.5 grid-cols-[repeat(auto-fill,minmax(112px,1fr))] w-full p-0 list-none mb-0 ">
                  {directSearchCategories.map(({ key, image, label }) => (
                    <li key={key}>
                      <button
                        type="button"
                        className="flex flex-col bg-transparent border-none"
                        onClick={() => {
                          setParameters({
                            ...searchParameters,
                            placeId: undefined,
                            collectionId: undefined,
                            searchType: "tag",
                            destinationLabel: undefined,
                            tag: key,
                            searchLocationType: undefined,
                          });

                          setIsLocationOpen(false);

                          if (
                            !searchParameters.arrivalDate ||
                            !searchParameters.departureDate ||
                            isFirstSearch
                          ) {
                            setIsCalendarOpen(true);
                            return;
                          }

                          if (
                            searchParameters.adults === DEFAULT_PARAMS.adults &&
                            searchParameters.childrenAge.length === 0
                          ) {
                            if (parent === "home") {
                              setIsGuestsOpen(true);
                            }
                          }
                        }}
                      >
                        <div className="size-28 p-1 shadow-xl rounded-2xl border border-gray-100 ">
                          <img
                            src={image}
                            alt={t(`ds.${label}`)}
                            className="rounded-xl w-full h-full"
                          />
                        </div>
                        <h4 className="text-black text-xs font-medium text-center w-full mt-2">
                          {t(`ds.${label}`)}
                        </h4>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {inputText !== "" && (
              <div className="flex flex-col h-full">
                {
                  <ul className="flex flex-col px-3 py-6 gap-4 flex-1 list-none lg:px-5 mb-0 lg:gap-2 overflow-y-auto max-h-[calc(100dvh-173px-64px)]">
                    {mode === "desktop" && (
                      <h4 className="text-[#747474] text-sm font-medium lg:mb-0">
                        {t("ds.destination_location")}
                      </h4>
                    )}
                    {isLoading &&
                      Array.from({ length: 3 }).map((_, index) => (
                        <li
                          key={index}
                          className="flex items-center gap-2 lg:p-2"
                        >
                          <div className="bg-[#e0e0e0] rounded-xl animate-pulse size-10" />
                          <div className="flex flex-col gap-1.5">
                            <div className="bg-[#e0e0e0] rounded-xl animate-pulse w-28 h-3.5" />
                            <div className="bg-[#e0e0e0] rounded-xl animate-pulse w-16 h-2.5" />
                          </div>
                          <div className="bg-[#e0e0e0] rounded-xl animate-pulse w-20 h-3 ml-auto" />
                        </li>
                      ))}
                    {!isLoading && (
                      <>
                        {locations.length > 0 &&
                          locationsPerCategory.places.map((location) => {
                            const isPlace = !!location.placeId;
                            const Icon = isPlace ? IconPin : IconStructure;

                            return (
                              <li
                                key={
                                  isPlace
                                    ? location.placeId
                                    : location.collectionId
                                }
                              >
                                <button
                                  type="button"
                                  className="flex items-center gap-2 bg-transparent border-none w-full lg:p-2 lg:hover:bg-[#F8F8F8] transition-colors duration-300 rounded-2xl"
                                  onClick={() => {
                                    setParameters({
                                      ...searchParameters,
                                      placeId: location.placeId,
                                      collectionId: location.collectionId,
                                      searchType: "city",
                                      searchLocationType: isPlace
                                        ? "known_location"
                                        : "hotel",
                                      destinationLabel: location.label,
                                      tag: undefined,
                                    });

                                    setIsLocationOpen(false);

                                    if (
                                      !searchParameters.arrivalDate ||
                                      !searchParameters.departureDate ||
                                      isFirstSearch
                                    ) {
                                      setIsCalendarOpen(true);
                                      return;
                                    }

                                    if (
                                      searchParameters.adults ===
                                        DEFAULT_PARAMS.adults &&
                                      searchParameters.childrenAge.length === 0
                                    ) {
                                      if (parent === "home") {
                                        setIsGuestsOpen(true);
                                      }
                                    }
                                  }}
                                >
                                  <div className="bg-[#F3F3F3] rounded-xl size-10 flex items-center justify-center">
                                    <Icon className="size-5 text-black" />
                                  </div>
                                  <div className="flex flex-col">
                                    <p className="text-sm font-medium text-left text-black">
                                      {location.label}
                                    </p>
                                    {location.secondaryLabel && (
                                      <p className="text-xs text-gray-400 text-left">
                                        {location.secondaryLabel}
                                      </p>
                                    )}
                                  </div>
                                  {/* {location.collections !== undefined &&
                                    location.collections > 0 && (
                                      <p className="ml-auto text-xs font-medium text-[#747474] text-left">
                                        {`${location.collections} ${t(
                                          "ds.structure",
                                          { count: location.collections },
                                        ).toLocaleLowerCase()}`}
                                      </p>
                                    )} */}
                                </button>
                              </li>
                            );
                          })}
                        {!isLoading &&
                          locationsPerCategory.extended.map((location) => (
                            <li key={location.placeId}>
                              <button
                                type="button"
                                className="flex items-center gap-2 bg-transparent border-none w-full lg:p-2 lg:hover:bg-[#F8F8F8] transition-colors duration-300 rounded-2xl"
                                onClick={() => {
                                  setParameters({
                                    ...searchParameters,
                                    placeId: location.placeId,
                                    collectionId: location.collectionId,
                                    searchType: "city",
                                    searchLocationType: "nearby_location",
                                    destinationLabel: location.label,
                                    tag: undefined,
                                  });

                                  setIsLocationOpen(false);

                                  if (
                                    !searchParameters.arrivalDate ||
                                    !searchParameters.departureDate ||
                                    isFirstSearch
                                  ) {
                                    setIsCalendarOpen(true);
                                    return;
                                  }

                                  if (
                                    searchParameters.adults ===
                                      DEFAULT_PARAMS.adults &&
                                    searchParameters.childrenAge.length === 0
                                  ) {
                                    if (parent !== "home") {
                                      setIsGuestsOpen(true);
                                    }
                                  }
                                }}
                              >
                                <div className="bg-[#F3F3F3] rounded-xl size-10 flex items-center justify-center">
                                  <IconCrosshair className="size-5 text-black" />
                                </div>
                                <div className="flex flex-col">
                                  <p className="text-sm font-medium text-left text-black">{`${
                                    location.label
                                  } ${
                                    location.nearby
                                      ? t("ds.destination_extended_nearby")
                                      : ""
                                  }`}</p>
                                </div>
                                {/* {location.collections !== undefined && (
                                  <p className="ml-auto text-xs font-medium text-[#747474] text-left">
                                    {`${location.collections} ${t(
                                      "ds.structure",
                                      {
                                        count: location.collections,
                                      },
                                    ).toLocaleLowerCase()}`}
                                  </p>
                                )} */}
                              </button>
                            </li>
                          ))}

                        {locations.length > 0 &&
                          locationsPerCategory.structures.map((location) => {
                            const isPlace = !!location.placeId;
                            const Icon = isPlace ? IconPin : IconStructure;

                            return (
                              <li
                                key={
                                  isPlace
                                    ? location.placeId
                                    : location.collectionId
                                }
                              >
                                <button
                                  type="button"
                                  className="flex items-center gap-2 bg-transparent border-none w-full lg:p-2 lg:hover:bg-[#F8F8F8] transition-colors duration-300 rounded-2xl"
                                  onClick={() => {
                                    setParameters({
                                      ...searchParameters,
                                      placeId: location.placeId,
                                      collectionId: location.collectionId,
                                      searchType: "city",
                                      searchLocationType: isPlace
                                        ? "known_location"
                                        : "hotel",
                                      destinationLabel: location.label,
                                      tag: undefined,
                                    });

                                    setIsLocationOpen(false);

                                    if (
                                      !searchParameters.arrivalDate ||
                                      !searchParameters.departureDate ||
                                      isFirstSearch
                                    ) {
                                      setIsCalendarOpen(true);
                                      return;
                                    }

                                    if (
                                      searchParameters.adults ===
                                        DEFAULT_PARAMS.adults &&
                                      searchParameters.childrenAge.length === 0
                                    ) {
                                      if (parent === "home") {
                                        setIsGuestsOpen(true);
                                      }
                                    }
                                  }}
                                >
                                  <div className="bg-[#F3F3F3] rounded-xl size-10 flex items-center justify-center">
                                    <Icon className="size-5 text-black" />
                                  </div>
                                  <div className="flex flex-col">
                                    <p className="text-sm font-medium text-left text-black">
                                      {location.label}
                                    </p>
                                    {location.secondaryLabel && (
                                      <p className="text-xs text-gray-400 text-left">
                                        {location.secondaryLabel}
                                      </p>
                                    )}
                                  </div>
                                  {/* {location.collections !== undefined &&
                                    location.collections > 0 && (
                                      <p className="ml-auto text-xs font-medium text-[#747474] text-left">
                                        {`${location.collections} ${t(
                                          "ds.structure",
                                          { count: location.collections },
                                        ).toLocaleLowerCase()}`}
                                      </p>
                                    )} */}
                                </button>
                              </li>
                            );
                          })}

                        {locationsPerCategory.places.length === 0 &&
                          locationsPerCategory.structures.length === 0 &&
                          locationsPerCategory.extended.length > 0 && (
                            <div className="flex flex-col items-center justify-center p-4 bg-[#F8F8F8] gap-3 rounded-2xl">
                              <IconSearchError className="size-5" />
                              <p className="text-sm font-medium text-center max-w-60 text-[#747474]">
                                {t("ds.destination_no_results_in_area")}
                              </p>
                            </div>
                          )}
                        {locationsPerCategory.places.length === 0 &&
                          locationsPerCategory.structures.length === 0 &&
                          locationsPerCategory.extended.length === 0 && (
                            <div className="flex flex-col items-center justify-center p-4 bg-[#F8F8F8] gap-3 rounded-2xl">
                              <IconSearchError className="size-5" />
                              <p className="text-sm font-medium text-center max-w-60 text-[#747474]">
                                {t("ds.destination_no_results")}
                              </p>
                            </div>
                          )}
                      </>
                    )}
                  </ul>
                }
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DestinationModal;
